import React, { Dispatch, useEffect, useCallback } from 'react';
import {
    Box,
    Divider,
    Stack,
    SxProps,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
    getDocumentMetadata,
    ProjectSidepanel,
} from '../../../redux/project/sliceProjectSidepanel';
import { IAppReduxStore } from '../../../redux/store';
import { headingFontBold } from '../../../Theme';
import { parseUsername } from '../../../util/DataUtils';
import LoadingData from './Components/LoadingData';
import PlaceholderCard from './Components/PlaceholderCard';

/**
 * Enum that describes systems for Permissions Tab
 */
enum Systems {
    System = 'System',
    General = 'General',
    WorkFlow = 'Workflow',
    Notice = 'Notice',
    AdHoc = 'AdHoc',
    Other = 'Other',
}

/**
 * Enum that describes permissions types for Permissions Tab
 */
enum PermissionsTypes {
    Edit = 'Author',
    Read = 'Reader',
}

/** Styles for the permissions section. */
const permissionsSectionSx: SxProps = {
    '&:not(:first-of-type)': {
        mt: 1.5,
    },
};

/** List of fields for header fieldset in getDocumentMetadata request */
const fields: string[] = [
    'AccessAuthorAdmin',
    'AccessAuthorGeneral',
    'AccessAuthorCategory',
    'AccessAuthorKeyword',
    'AccessAuthorPackage',
    'wfEditor',
    'AccessAuthorOther',
    'AccessReaderAdmin',
    'AccessReaderGeneral',
    'AccessReaderCategory',
    'AccessReaderKeyword',
    'AccessReaderPackage',
    'AccessReaderNotice',
    'AccessReaderAdHoc',
    'AccessReaderOther',
];

/**
 * Sidepanel that shows information about read and edit rights on a given document.
 */
export default () => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'sidepanel' });

    /** Allows reducers on a redux slice to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();

    /** Get the replicaId and viewName from the react router. */
    const { replicaId, viewName } = useParams();

    /** Get relevant values from Redux store. */
    const {
        data,
        loading,
        targetDocId: universalId,
    } = useSelector((store: IAppReduxStore) => store.projectSidepanel);

    /**
     * Effect that fetches the relevant document data for displaying in the sidepanel.
     */
    useEffect(() => {
        !data[ProjectSidepanel.Permissions] &&
            universalId &&
            dispatch(
                getDocumentMetadata({
                    replicaId,
                    viewName,
                    universalId,
                    fields: fields,
                    tab: ProjectSidepanel.Permissions,
                }),
            );
    }, [universalId, data]);

    /** Access the document's general infomation from the Redux store. */
    const sidepanelData = data[ProjectSidepanel.Permissions];

    /**
     * Checks the permissions data on an array and parses user's name.
     * @param permissionsData - Permissions according to systems.
     * @returns Array of permissions.
     */
    const checkOnArray = (permissionsData?: string[] | string): string[] => {
        if (!permissionsData) {
            return [];
        }
        if (Array.isArray(permissionsData)) {
            return permissionsData.map(item => parseUsername(item));
        }
        return [parseUsername(permissionsData)];
    };

    /**
     * Handles permissions data form sidepanelData in accordance with each system.
     * @param arr - Array of permissions from every system.
     * @returns Processed array of permissions.
     */
    const handlePermissionsData = (arr: string[]) => {
        return arr
            .filter(item => sidepanelData[item])
            .map(item => checkOnArray(sidepanelData[item]));
    };

    /**
     * Provides the object of permissions depending on the permissions type.
     * @param permissionsType - Permissions type.
     * @returns Object of permissions.
     */
    const getFields = (permissionsType: PermissionsTypes): Record<Systems, string[][]> => ({
        [Systems.System]: handlePermissionsData([`Access${permissionsType}Admin`]),
        [Systems.General]: handlePermissionsData([
            `Access${permissionsType}General`,
            `Access${permissionsType}Category`,
            `Access${permissionsType}Keyword`,
            `Access${permissionsType}Package`,
        ]),
        [Systems.WorkFlow]: handlePermissionsData([`wfEditor`]),
        [Systems.Notice]: handlePermissionsData([`Access${permissionsType}Notice`]),
        [Systems.AdHoc]: handlePermissionsData([`Access${permissionsType}AdHoc`]),
        [Systems.Other]: handlePermissionsData([`Access${permissionsType}Other`]),
    });

    /**
     * Creates the permissions section depending on the permissions type.
     */
    const createPermissionsSection = useCallback(
        (permissionsType: PermissionsTypes) => {
            const config = getFields(permissionsType);
            return (
                <Stack key={permissionsType} sx={permissionsSectionSx}>
                    <Typography
                        variant='h6'
                        sx={{
                            fontSize: 16,
                            py: 1,
                        }}
                    >
                        {t(permissionsType.toLowerCase())}
                    </Typography>
                    <Table size='small' sx={{ tableLayout: 'fixed' }}>
                        <TableBody>
                            {Object.keys(config).map(
                                r =>
                                    config[r].length !== 0 && (
                                        <TableRow key={`${permissionsType}_${r}`}>
                                            <TableCell
                                                sx={{
                                                    ...headingFontBold,
                                                    whiteSpace: 'nowrap',
                                                    px: 0,
                                                    width: '40%',
                                                }}
                                            >
                                                {r === 'System' ? 'System' : t(r.toLowerCase())}
                                            </TableCell>

                                            <TableCell sx={{ pr: 0 }}>
                                                <Box sx={{ maxHeight: 150, overflowY: 'auto' }}>
                                                    {config[r].map((x: string[]) => {
                                                        return x.map((value: string, i: number) => (
                                                            <Box key={`${x}_${i + 1}`}>{value}</Box>
                                                        ));
                                                    })}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ),
                            )}
                        </TableBody>
                    </Table>
                </Stack>
            );
        },
        [sidepanelData],
    );

    return (
        <>
            {loading && <LoadingData />}
            {!loading && !sidepanelData && <PlaceholderCard translationKey='NoInfoPlaceholder' />}
            {sidepanelData && (
                <Stack divider={<Divider flexItem />} sx={{ px: 1, pb: 1, overflow: 'auto' }}>
                    {Object.values(PermissionsTypes).map(permissionsType =>
                        createPermissionsSection(permissionsType),
                    )}
                </Stack>
            )}
        </>
    );
};
